import { useEffect, useRef, useState } from "react";

const HOLD_MS = 1150;
const OPEN_MS = 1400;
const PROBLEM_MS = 1700;
const RESOLVE_MS = 1300;
const REVEAL_MS = 700;
const UNMOUNT_MS = 500;

type Phase =
  | "closed"
  | "opening"
  | "problem1"
  | "problem2"
  | "resolve"
  | "reveal"
  | "done";

const TIMELINE: { phase: Phase; delay: number }[] = [
  { phase: "opening", delay: HOLD_MS },
  { phase: "problem1", delay: OPEN_MS },
  { phase: "problem2", delay: PROBLEM_MS },
  { phase: "resolve", delay: PROBLEM_MS },
  { phase: "reveal", delay: RESOLVE_MS },
  { phase: "done", delay: REVEAL_MS },
];

// Splits a line into words that fade + rise in with a stagger, instead of
// the whole sentence appearing at once — reads as a more deliberate,
// editorial reveal for the two pain-point lines specifically.
function StaggeredWords({
  text,
  startMs,
  stepMs = 60,
}: {
  text: string;
  startMs: number;
  stepMs?: number;
}) {
  const words = text.split(" ");
  return (
    <>
      {words.map((word, i) => (
        <span
          key={i}
          className="tw-inline-block"
          style={{
            opacity: 0,
            animation: `word-in 480ms ease-out ${startMs + i * stepMs}ms forwards`,
          }}
        >
          {word}
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </>
  );
}

/**
 * Curtain-open intro that plays before the hero on every load/refresh.
 * Panels split apart onto a dark "problem" screen (same tone as the
 * curtain, so the split reads seamless) that cycles two pain-point lines
 * and a one-line resolution before fading away to reveal the hero already
 * rendered underneath. Skipped entirely for prefers-reduced-motion; a
 * click/tap anywhere fast-forwards straight to the reveal.
 */
export default function IntroOverlay() {
  const [shouldRender, setShouldRender] = useState(false);
  const [phase, setPhase] = useState<Phase>("closed");
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    setShouldRender(true);

    let elapsed = 0;
    const timers = TIMELINE.map((step) => {
      elapsed += step.delay;
      return setTimeout(() => setPhase(step.phase), elapsed);
    });
    timersRef.current = timers;
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== "done") return;
    const timer = setTimeout(() => setShouldRender(false), UNMOUNT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (!shouldRender) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [shouldRender]);

  const handleSkip = () => {
    if (phase === "reveal" || phase === "done") return;
    timersRef.current.forEach(clearTimeout);
    setPhase("reveal");
    timersRef.current = [setTimeout(() => setPhase("done"), REVEAL_MS)];
  };

  if (!shouldRender) return null;

  const curtainOpen = phase !== "closed";
  const ending = phase === "reveal" || phase === "done";
  const screenOpacity = phase === "closed" ? 0 : ending ? 0 : 1;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-cursor-pointer"
      style={{ pointerEvents: phase === "done" ? "none" : "auto" }}
      onClick={handleSkip}
      aria-hidden="true"
    >
      <div
        className="tw-absolute tw-inset-0 tw-bg-[#151515]"
        style={{
          opacity: screenOpacity,
          transition: `opacity ${ending ? REVEAL_MS : 0}ms ease`,
        }}
      >
        <div
          className="tw-absolute tw-inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 42%, rgba(254,229,0,0.1), transparent 55%)",
          }}
        />
        <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-px-6">
          {phase === "problem1" && (
            <p
              key="problem1"
              className="tw-max-w-2xl tw-break-keep tw-text-center tw-text-2xl tw-font-semibold tw-leading-snug tw-text-white md:tw-text-4xl"
            >
              <StaggeredWords text="신규 고객 유치에 대한" startMs={0} />
              <br />
              <StaggeredWords text="마케팅 고민 있으셨나요?" startMs={260} />
            </p>
          )}
          {phase === "problem2" && (
            <p
              key="problem2"
              className="tw-max-w-2xl tw-break-keep tw-text-center tw-text-2xl tw-font-semibold tw-leading-snug tw-text-white md:tw-text-4xl"
            >
              <StaggeredWords text="우리 브랜드에 적합한 타겟에게" startMs={0} />
              <br />
              <StaggeredWords text="마케팅 메시지를 보내고 싶으셨나요?" startMs={320} />
            </p>
          )}
          {phase === "resolve" && (
            <div key="resolve" className="tw-relative tw-flex tw-items-center tw-justify-center">
              <div
                className="tw-pointer-events-none tw-absolute tw-h-[280px] tw-w-[280px] tw-rounded-full md:tw-h-[420px] md:tw-w-[420px]"
                style={{
                  background:
                    "radial-gradient(circle, rgba(254,229,0,0.35), transparent 70%)",
                  animation: "resolve-glow-burst 900ms ease-out forwards",
                }}
              />
              <p
                className="tw-relative tw-max-w-2xl tw-break-keep tw-text-center tw-text-2xl tw-font-semibold tw-leading-snug tw-text-white md:tw-text-4xl"
                style={{ animation: "resolve-in 650ms cubic-bezier(0.19, 1, 0.22, 1) forwards" }}
              >
                카카오 신규 상품,
                <br />
                <span className="tw-text-[#FEE500]">카카오 브랜드픽</span>이 답을 찾아드립니다
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sits under the two curtain panels — invisible while closed, then
          briefly flares as the panels part, like light catching the seam. */}
      <div
        className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-0 tw-h-full"
        style={{
          transform: "translateX(-50%)",
          background:
            "linear-gradient(180deg, transparent, rgba(255,255,255,0.95) 45%, rgba(254,229,0,0.9) 55%, transparent)",
          opacity: curtainOpen ? undefined : 0,
          animation: curtainOpen ? "seam-flare 900ms ease-out forwards" : "none",
        }}
      />

      <div
        className="tw-relative tw-h-full tw-w-1/2 tw-overflow-hidden tw-bg-[#151515]"
        style={{
          transform: `translateX(${curtainOpen ? -100 : 0}%)`,
          transition: `transform ${OPEN_MS}ms cubic-bezier(0.76, 0, 0.24, 1)`,
        }}
      >
        <div
          className="tw-absolute tw-inset-0"
          style={{
            background:
              "radial-gradient(circle at 100% 50%, rgba(254,229,0,0.16), transparent 46%)",
          }}
        />
      </div>
      <div
        className="tw-relative tw-h-full tw-w-1/2 tw-overflow-hidden tw-bg-[#151515]"
        style={{
          transform: `translateX(${curtainOpen ? 100 : 0}%)`,
          transition: `transform ${OPEN_MS}ms cubic-bezier(0.76, 0, 0.24, 1)`,
        }}
      >
        <div
          className="tw-absolute tw-inset-0"
          style={{
            background:
              "radial-gradient(circle at 0% 50%, rgba(254,229,0,0.16), transparent 46%)",
          }}
        />
      </div>

      <div
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4"
        style={{
          opacity: curtainOpen ? 0 : 1,
          transform: `scale(${curtainOpen ? 1.06 : 1})`,
          transition: "opacity 480ms ease, transform 480ms ease",
        }}
      >
        <img
          src="/biztalk-logo.svg"
          alt="비즈톡"
          className="tw-h-8 tw-w-auto md:tw-h-10"
          style={{ opacity: 0, animation: "intro-logo-in 700ms ease-out 250ms forwards" }}
        />
        <span
          className="tw-h-px tw-bg-[#FEE500]"
          style={{ width: 0, animation: "intro-line-draw 450ms ease-out 700ms forwards" }}
        />
      </div>

      <button
        type="button"
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          handleSkip();
        }}
        className="tw-absolute tw-bottom-6 tw-right-6 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-white/45 tw-transition-colors hover:tw-text-white/80"
        style={{
          opacity: phase === "closed" || ending ? 0 : 1,
          transition: "opacity 300ms ease, color 200ms ease",
        }}
      >
        건너뛰기
      </button>
    </div>
  );
}
